import { forwardRef, useEffect, createElement, useCallback, useState } from 'react'
import { Map } from "mapbox-gl";
import bbox from '@turf/bbox'
import LightOn from '../icon/light-on.png'
import LightOff from '../icon/light-off.png'
import FcuOff from '../icon/fcu-off.png'
import FcuOn from '../icon/fcu-on.png'
import FcuWarning from '../icon/fcu-warning.png'
import FcuError from '../icon/fcu-error.png'
import FloorplanDark from '../map-styles/floorplan-dark';
import FloorplanLight from '../map-styles/floorplan-light'
import { FloorplanType } from '../interface-type'

const Floorplan = forwardRef((props: FloorplanType, ref: any) => {
    const [map, setmap] = useState<Map | any | null>(null)
    const [prevPlan, setprevPlan] = useState<any>(null)
    const [prevDevice, setprevDevice] = useState<any>(null)

    const updateFloorplan = useCallback(
        async () => {
            if (!prevPlan && props.plan) {
                const _bbox = bbox(props.plan.data)
                map.fitBounds(_bbox, {
                    linear: false, easing: (t: any) => t / t,
                });
                map.addSource(`floorplan-source`, { type: "geojson", data: props.plan.data });
                map.addLayer({
                    id: `open-space`,
                    type: "fill",
                    source: `floorplan-source`,
                    filter: ['any', ["==", "type", "open-space"], ['==', 'type', 'staircase'], ['==', 'type', 'lift']],
                    paint: {
                        "fill-color": "#ECDFB4",
                        "fill-opacity": 1
                    }
                });
                map.addLayer({
                    id: `open-space-line`,
                    type: "line",
                    source: `floorplan-source`,
                    filter: ['any', ["==", "type", "open-space"], ['==', 'type', 'staircase'], ['==', 'type', 'lift']],
                    paint: {
                        "line-color": "#fff",
                        "line-opacity": 1
                    }
                });
                map.addLayer({
                    id: `room-line`,
                    type: "line",
                    source: `floorplan-source`,
                    filter: ["==", "type", "room"],
                    paint: {
                        "line-color": "#fff",
                        "line-opacity": 1
                    }
                });
                map.addLayer({
                    id: `room`,
                    type: "fill-extrusion",
                    source: `floorplan-source`,
                    filter: ["==", "type", "room"],
                    paint: {
                        "fill-extrusion-color": "#fff",
                        "fill-extrusion-height": 1,
                        "fill-extrusion-base": 0,
                        "fill-extrusion-opacity": 0.8
                    }
                });
            } else if (prevPlan && props.plan) {
                const _bbox = bbox(props.plan.data)
                map.fitBounds(_bbox, {
                    linear: false, easing: (t: any) => t / t
                });
                map.getSource('floorplan-source').setData(props.plan.data)
            } else {
                if (map && prevPlan) {
                    const mapLayer = map.getStyle().layers.filter((l: any) => l.source === 'floorplan-source')
                    mapLayer.map((l: any, i: number) => {
                        map.removeLayer(l.id)
                        if (mapLayer.length === i + 1) {
                            map.removeSource('floorplan-source')
                        }
                    })
                }
            }
            setprevPlan(props.plan)
        },
        [props.plan, prevPlan, map],
    )

    const onMouseenter = () => {
        map.getCanvas().style.cursor = 'pointer';
    }
    const onMouseleave = () => {
        map.getCanvas().style.cursor = '';
    }
    const onClickFeature = (e: any) => {
        props.onClickDevice(e.features[0], props.device)
    }

    const updateDevice = useCallback(
        async () => {
            if (!prevDevice && props.device) {
                map.addSource(`device-source`, { type: "geojson", data: props.device.data });
                map.addLayer({
                    "id": "device-light-ch1",
                    "source": "device-source",
                    "type": "symbol",
                    'filter': ['all', ['==', 'type', 'Lighting']],
                    "layout": {
                        "icon-image": [
                            'case',
                            ['==', ['get', 'CH1'], 0],
                            'light-off',
                            ['==', ['get', 'CH1'], 1],
                            'light-on',
                            '' // default
                        ],
                        "icon-size": 0.2,
                        'icon-offset': [-150, 0]
                    }
                });
                map.addLayer({
                    "id": "device-light-ch2",
                    "source": "device-source",
                    "type": "symbol",
                    'filter': ['all', ['==', 'type', 'Lighting']],
                    "layout": {
                        "icon-image": [
                            'case',
                            ['==', ['get', 'CH2'], 0],
                            'light-off',
                            ['==', ['get', 'CH2'], 1],
                            'light-on',
                            '' // default
                        ],
                        "icon-size": 0.2
                    }
                });
                map.addLayer({
                    "id": "device-light-ch3",
                    "source": "device-source",
                    "type": "symbol",
                    'filter': ['all', ['==', 'type', 'Lighting']],
                    "layout": {
                        "icon-image": [
                            'case',
                            ['==', ['get', 'CH3'], 0],
                            'light-off',
                            ['==', ['get', 'CH3'], 1],
                            'light-on',
                            '' // default
                        ],
                        "icon-size": 0.2,
                        'icon-offset': [150, 0]
                    }
                });
                map.addLayer({
                    "id": "device-a/c",
                    "source": "device-source",
                    "type": "symbol",
                    'filter': ['all', ['==', 'type', 'A/C']],
                    "layout": {
                        "icon-image": [
                            'case',
                            ['all', ['==', ['get', 'pow'], 0], ['==', ['get', 'err'], '00']], 'fcu-off',
                            ['all', ['==', ['get', 'pow'], 1], ['==', ['get', 'err'], '00']], 'fcu-on',
                            ['!=', ['get', 'err'], '00'], 'fcu-error',
                            'fcu-warning' // default
                        ],
                        "icon-size": 0.2
                    }
                });
                map.on('click', 'device-light-ch1', onClickFeature)
                map.on('click', 'device-light-ch2', onClickFeature)
                map.on('click', 'device-light-ch3', onClickFeature)
                map.on('click', 'device-a/c', onClickFeature)
                map.on('mouseenter', 'device-light-ch1', onMouseenter);
                map.on('mouseenter', 'device-light-ch2', onMouseenter);
                map.on('mouseenter', 'device-light-ch3', onMouseenter);
                map.on('mouseenter', 'device-a/c', onMouseenter);
                map.on('mouseleave', 'device-light-ch1', onMouseleave);
                map.on('mouseleave', 'device-light-ch2', onMouseleave);
                map.on('mouseleave', 'device-light-ch3', onMouseleave);
                map.on('mouseleave', 'device-a/c', onMouseleave);
            }
            else if (prevDevice && props.device) {
                map.getSource('device-source').setData(props.device.data)
                map.on('click', 'device-light-ch1', onClickFeature)
                map.on('click', 'device-light-ch2', onClickFeature)
                map.on('click', 'device-light-ch3', onClickFeature)
                map.on('click', 'device-a/c', onClickFeature)
                map.on('mouseenter', 'device-light-ch1', onMouseenter);
                map.on('mouseenter', 'device-light-ch2', onMouseenter);
                map.on('mouseenter', 'device-light-ch3', onMouseenter);
                map.on('mouseenter', 'device-a/c', onMouseenter);
                map.on('mouseleave', 'device-light-ch1', onMouseleave);
                map.on('mouseleave', 'device-light-ch2', onMouseleave);
                map.on('mouseleave', 'device-light-ch3', onMouseleave);
                map.on('mouseleave', 'device-a/c', onMouseleave);
            } else {
                if (map && prevDevice) {
                    const mapLayer = map.getStyle().layers.filter((l: any) => l.source === 'device-source')
                    mapLayer.map((l: any, i: number) => {
                        map.removeLayer(l.id)
                        if (mapLayer.length === i + 1) {
                            map.removeSource('device-source')
                        }
                    })
                }
            }

            setprevDevice(props.device)
        },
        [props.device, prevDevice, map],
    )

    useEffect(() => {
        if (map) {
            map.setPaintProperty('background', 'background-color', props.theme === 'light' ? '#fff' : '#2a3640');
        }
    }, [props.theme])

    useEffect(() => {
        if (map) {
            updateFloorplan()
        }
    }, [map, updateFloorplan])

    useEffect(() => {
        if (map) {
            updateDevice()
        }
        return () => {
            if (map) {
                const mapLayer = map.getStyle().layers.filter((l: any) => l.source === 'device-source')
                mapLayer.map((l: any) => {
                    map.off('click', l.id, onClickFeature);
                    map.off('mouseenter', l.id, onMouseenter);
                    map.off('mouseleave', l.id, onMouseleave);
                })
            }
        }
    }, [map, updateDevice])


    useEffect(() => {
        if (!map) {
            const mapRender = new Map({
                container: ref.current,
                style: props.theme === 'light' ? FloorplanLight : FloorplanDark,
                center: [0, 0],
                zoom: 10,
                pitch: 60,
                bearing: 20
            });
            mapRender.on('load', async (e) => {
                e.target.loadImage(LightOff, (error: any, image: any) => {
                    if (error) throw error;
                    e.target.addImage('light-off', image);
                    e.target.loadImage(LightOn, (error: any, image: any) => {
                        if (error) throw error;
                        e.target.addImage('light-on', image);
                        e.target.loadImage(FcuOff, (error: any, image: any) => {
                            if (error) throw error;
                            e.target.addImage('fcu-off', image);
                            e.target.loadImage(FcuOn, (error: any, image: any) => {
                                if (error) throw error;
                                e.target.addImage('fcu-on', image);
                                e.target.loadImage(FcuError, (error: any, image: any) => {
                                    if (error) throw error;
                                    e.target.addImage('fcu-error', image);
                                    e.target.loadImage(FcuWarning, (error: any, image: any) => {
                                        if (error) throw error;
                                        e.target.addImage('fcu-warning', image);
                                        setmap(e.target)
                                        props.onMapLoaded(e.target)
                                        ref.current.mapRender = e.target;
                                    })
                                })
                            })

                        })
                    })
                })
            })

        }
        return () => { };
    }, [ref]);

    return createElement("div", {
        style: { height: "100%", width: "100%" },
        ref: ref
    });
})

Floorplan.defaultProps = {
    plan: null,
    device: null,
    theme: 'dark',
    onMapLoaded: () => { },
    onClickDevice: () => { }
}

export default Floorplan
